import type { ProviderListingReference } from "@/features/providers";

export type OtherDog = {
    id: string;
    name: string;
    imageUrl: string | null;
    breedName: string | undefined;
    ageLabel: string | null;
};

export const MIN_OTHER_DOGS = 2;
const MIN_LITTERMATES = 2;
const MAX_SHOWN = 8;
const LARGE_PROVIDER = 50;

function sharesBreed(reference: ProviderListingReference, breeds: Set<string>) {
    return reference.breedNames.some((name) => breeds.has(name.toLowerCase()));
}

export function otherDogsFromReferences({
    references,
    availableCount,
    listing,
}: {
    references: ProviderListingReference[];
    availableCount: number;
    listing: {
        id: string;
        status: string;
        ageLabel: string | undefined;
        breedNames: string[];
    };
}): { dogs: OtherDog[]; availableOthers: number; littermates: number } {
    const seen = new Set<string>();
    const others = references.filter((reference) => {
        if (reference.listingId === listing.id) return false;
        if (reference.status !== "available") return false;
        if (seen.has(reference.listingId)) return false;
        seen.add(reference.listingId);
        return true;
    });
    const breeds = new Set(
        listing.breedNames.map((name) => name.toLowerCase()),
    );
    const isLittermate = (reference: ProviderListingReference) =>
        listing.ageLabel !== undefined &&
        reference.ageLabel === listing.ageLabel &&
        sharesBreed(reference, breeds);
    const littermates = others.filter(isLittermate);
    const ordered = [
        ...littermates,
        ...others.filter((reference) => !isLittermate(reference)),
    ];
    const availableOthers = Math.max(
        others.length,
        availableCount - (listing.status === "available" ? 1 : 0),
    );
    return {
        dogs: ordered.slice(0, MAX_SHOWN).map((reference) => ({
            id: reference.listingId,
            name: reference.name.trim() || "Unnamed dog",
            imageUrl: reference.primaryImageUrl,
            breedName: reference.breedNames[0],
            ageLabel: reference.ageLabel,
        })),
        availableOthers,
        littermates: littermates.length,
    };
}

export function otherDogsLabel({
    dogName,
    availableOthers,
    littermates,
    providerKind,
}: {
    dogName: string;
    availableOthers: number;
    littermates: number;
    providerKind: "breeder" | "shelter";
}) {
    if (availableOthers > LARGE_PROVIDER) return "Their dogs";
    if (providerKind === "breeder" && littermates >= MIN_LITTERMATES)
        return `${dogName} and littermates`;
    return `${dogName} and ${availableOthers} more`;
}
